import React from 'react';
import { Box, Typography } from '@mui/material';
import AuthLegalFooter from './AuthLegalFooter';
import AuthPreferencesBar from './AuthPreferencesBar';
import VisiMeAuthLogo from './VisiMeAuthLogo';
import { authBackgroundSx, authGlassCardSx, authScreen } from './authScreenStyles';
import { useT } from '../../i18n/useT';

type Props = {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  stepKey: string;
};

export default function AuthScreenShell({ title, subtitle, children, stepKey }: Props) {
  const t = useT();

  return (
    <Box
      sx={{
        ...authBackgroundSx,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 5,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <VisiMeAuthLogo height={40} />
        <Box sx={{ maxWidth: 360 }}>
          <Typography
            sx={{
              fontFamily: authScreen.font,
              fontWeight: 600,
              fontSize: '2.25rem',
              lineHeight: 1.15,
              letterSpacing: '-0.03em',
              mb: 1.5,
            }}
          >
            {t('loginWelcome')}
          </Typography>
          <Typography sx={{ fontFamily: authScreen.font, fontSize: '1.0625rem', color: authScreen.textMuted, lineHeight: 1.55 }}>
            {t('loginSubtitle')}
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: authScreen.font, fontSize: '0.75rem', color: authScreen.textDim }}>
          © {new Date().getFullYear()} {t('brandName')}
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 2, sm: 3 },
          py: { xs: 2.5, sm: 4 },
          justifyContent: 'center',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: { xs: 2, lg: 0 }, lg: { position: 'absolute', top: 24, right: 24 } }}>
          <AuthPreferencesBar />
        </Box>

        <Box sx={{ width: '100%', maxWidth: authScreen.maxContentWidth, mx: 'auto' }}>
          <Box sx={{ display: { xs: 'block', lg: 'none' }, mb: 3 }}>
            <VisiMeAuthLogo height={34} />
          </Box>

          <Box sx={authGlassCardSx}>
            <Box
              key={stepKey}
              sx={{
                mb: 2.5,
                animation: 'authStepIn 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
                '@keyframes authStepIn': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'none' },
                },
              }}
            >
              <Typography
                component="h1"
                sx={{
                  fontFamily: authScreen.font,
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '1.625rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.025em',
                }}
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  sx={{
                    display: { xs: 'block', lg: 'none' },
                    mt: 0.75,
                    fontFamily: authScreen.font,
                    fontSize: '0.9375rem',
                    color: authScreen.textMuted,
                    lineHeight: 1.5,
                  }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>

            {children}

            <AuthLegalFooter />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
